import {
  AxisStructuredDataIndustry,
  AxisStructuredDataSubIndustry,
  AxisStructuredDataTypeEnum,
} from '../../gql/graphql';

export const AXIS_NEW_CLIENT_FORM_INTERNAL_NAMES = {
  commercialOrBenefits:
    'axis_general_inquiry_new_client_commercial_or_benefits',
  personalLifeOrWealth:
    'axis_general_inquiry_new_client_personal_life_or_wealth',
  lwbBenefitsOrWealth: 'axis_general_inquiry_new_client_lwb_benefits_or_wealth',
  engineers: 'axis_general_inquiry_new_client_engineers',
  engineersApplication: 'axis_engineers_application',
  architects: 'axis_general_inquiry_new_client_architects',
  architectsApplication: 'axis_architects_application',
};

export type ClientType =
  | AxisStructuredDataTypeEnum.Commercial
  | AxisStructuredDataTypeEnum.Personal
  | AxisStructuredDataTypeEnum.Lwb;

export type ContractorSubIndustryId =
  | AxisStructuredDataSubIndustry.Electricians
  | AxisStructuredDataSubIndustry.DryWall
  | AxisStructuredDataSubIndustry.Painters
  | AxisStructuredDataSubIndustry.Concretors
  | AxisStructuredDataSubIndustry.Hvac
  | AxisStructuredDataSubIndustry.Plumber
  | AxisStructuredDataSubIndustry.Roofer
  | AxisStructuredDataSubIndustry.GeneralContractor
  | AxisStructuredDataSubIndustry.Other;

export const CLIENT_TYPE_IDS: Record<string, ClientType> = {
  commercial: AxisStructuredDataTypeEnum.Commercial,
  personal: AxisStructuredDataTypeEnum.Personal,
  lwb: AxisStructuredDataTypeEnum.Lwb,
};

export type ClientTypeOption = {
  id: ClientType;
  label: string;
};

export const CLIENT_TYPE_OPTIONS: Array<ClientTypeOption> = [
  { id: CLIENT_TYPE_IDS.commercial, label: 'Commercial' },
  { id: CLIENT_TYPE_IDS.personal, label: 'Personal' },
  { id: CLIENT_TYPE_IDS.lwb, label: 'Life, Benefits, & Wealth' },
];

export type SelectableOption<OptionId extends string = string> = {
  id: OptionId;
  label: string;
};

export type AxisIndustryId = AxisStructuredDataIndustry;
export type AxisSubIndustryId = AxisStructuredDataSubIndustry;

export type CommercialIndustryOption = SelectableOption<AxisIndustryId> & {
  subIndustries?: Array<SelectableOption<AxisSubIndustryId>>;
};

export const PERSONAL_INDUSTRY_IDS = {
  travelInsurance: AxisStructuredDataIndustry.TravelInsurance,
  autoInsurance: AxisStructuredDataIndustry.AutoInsurance,
  homeInsurance: AxisStructuredDataIndustry.HomeInsurance,
  privateAviation: AxisStructuredDataIndustry.PrivateAviation,
  other: AxisStructuredDataIndustry.Other,
};

export const PERSONAL_INDUSTRY_OPTIONS: Array<
  SelectableOption<AxisIndustryId>
> = [
  { id: PERSONAL_INDUSTRY_IDS.travelInsurance, label: 'Travel Insurance' },
  { id: PERSONAL_INDUSTRY_IDS.autoInsurance, label: 'Auto Insurance' },
  { id: PERSONAL_INDUSTRY_IDS.homeInsurance, label: 'Home Insurance' },
  { id: PERSONAL_INDUSTRY_IDS.privateAviation, label: 'Private Aviation' },
  { id: PERSONAL_INDUSTRY_IDS.other, label: 'Other' },
];

export const LWB_INDUSTRY_IDS = {
  lifeInsurance: AxisStructuredDataIndustry.LifeInsurance,
  benefits: AxisStructuredDataIndustry.Benefits,
  wealthInsurance: AxisStructuredDataIndustry.WealthInsurance,
  other: AxisStructuredDataIndustry.Other,
};

export const LWB_INDUSTRY_OPTIONS: Array<SelectableOption<AxisIndustryId>> = [
  { id: LWB_INDUSTRY_IDS.lifeInsurance, label: 'Life Insurance' },
  { id: LWB_INDUSTRY_IDS.benefits, label: 'Group Benefits' },
  { id: LWB_INDUSTRY_IDS.wealthInsurance, label: 'Group Retirement' },
  { id: LWB_INDUSTRY_IDS.other, label: 'Other' },
];

export const COMMERCIAL_INDUSTRY_IDS = {
  agricultureFood: AxisStructuredDataIndustry.AgricultureFood,
  aviation: AxisStructuredDataIndustry.Aviation,
  cannabis: AxisStructuredDataIndustry.Cannabis,
  construction: AxisStructuredDataIndustry.Construction,
  contractors: AxisStructuredDataIndustry.Contractors,
  energy: AxisStructuredDataIndustry.Energy,
  international: AxisStructuredDataIndustry.International,
  lifeSciences: AxisStructuredDataIndustry.LifeSciences,
  manufacturing: AxisStructuredDataIndustry.Manufacturing,
  mining: AxisStructuredDataIndustry.Mining,
  nonProfit: AxisStructuredDataIndustry.NonProfit,
  professionalFinancialServices:
    AxisStructuredDataIndustry.ProfessionalFinancialServices,
  publicSector: AxisStructuredDataIndustry.PublicSector,
  realEstate: AxisStructuredDataIndustry.RealEstate,
  technology: AxisStructuredDataIndustry.Technology,
  transportation: AxisStructuredDataIndustry.Transportation,
  other: AxisStructuredDataIndustry.Other,
};

export const CONTRACTORS_INDUSTRY_ID = COMMERCIAL_INDUSTRY_IDS.contractors;

export const PROFESSIONAL_FINANCIAL_SERVICES_INDUSTRY_ID =
  COMMERCIAL_INDUSTRY_IDS.professionalFinancialServices;

export const CONTRACTOR_SUB_INDUSTRY_IDS = {
  electricians: AxisStructuredDataSubIndustry.Electricians,
  dryWall: AxisStructuredDataSubIndustry.DryWall,
  painters: AxisStructuredDataSubIndustry.Painters,
  concretors: AxisStructuredDataSubIndustry.Concretors,
  hvac: AxisStructuredDataSubIndustry.Hvac,
  plumber: AxisStructuredDataSubIndustry.Plumber,
  roofer: AxisStructuredDataSubIndustry.Roofer,
  generalContractor: AxisStructuredDataSubIndustry.GeneralContractor,
  other: AxisStructuredDataSubIndustry.Other,
};

export const EXTENDED_APPLICATION_CONTRACTOR_SUB_INDUSTRY_IDS: Array<AxisSubIndustryId> =
  [
    CONTRACTOR_SUB_INDUSTRY_IDS.electricians,
    CONTRACTOR_SUB_INDUSTRY_IDS.dryWall,
    CONTRACTOR_SUB_INDUSTRY_IDS.painters,
    CONTRACTOR_SUB_INDUSTRY_IDS.concretors,
  ];

export const isExtendedApplicationContractorSubIndustry = (
  subIndustryId: AxisSubIndustryId | null,
): boolean =>
  subIndustryId !== null &&
  EXTENDED_APPLICATION_CONTRACTOR_SUB_INDUSTRY_IDS.includes(subIndustryId);

export const CONTRACTOR_BUSINESS_INTERNAL_NAMES = {
  electricians: 'contractor_electricians',
  dryWall: 'contractor_dry_wall',
  painters: 'contractor_painters',
  concretors: 'contractor_concretors',
  hvac: 'contractor_hvac',
  plumber: 'contractor_plumber',
  roofer: 'contractor_roofer',
  generalContractor: 'contractor_general_contractor',
  other: 'contractor_other',
};

export const CONTRACTOR_SUB_INDUSTRY_OPTIONS: Array<
  SelectableOption<AxisSubIndustryId>
> = [
  {
    id: CONTRACTOR_SUB_INDUSTRY_IDS.electricians,
    label: 'Electricians',
  },
  { id: CONTRACTOR_SUB_INDUSTRY_IDS.dryWall, label: 'Dry-wall' },
  { id: CONTRACTOR_SUB_INDUSTRY_IDS.painters, label: 'Painters' },
  { id: CONTRACTOR_SUB_INDUSTRY_IDS.concretors, label: 'Concreters' },
  { id: CONTRACTOR_SUB_INDUSTRY_IDS.hvac, label: 'HVAC' },
  { id: CONTRACTOR_SUB_INDUSTRY_IDS.plumber, label: 'Plumber' },
  { id: CONTRACTOR_SUB_INDUSTRY_IDS.roofer, label: 'Roofer' },
  {
    id: CONTRACTOR_SUB_INDUSTRY_IDS.generalContractor,
    label: 'General Contractor',
  },
  { id: CONTRACTOR_SUB_INDUSTRY_IDS.other, label: 'Other' },
];

export const CONTRACTOR_SUB_INDUSTRY_TO_BUSINESS_INTERNAL_NAME: Partial<
  Record<AxisSubIndustryId, string>
> = {
  [CONTRACTOR_SUB_INDUSTRY_IDS.electricians]:
    CONTRACTOR_BUSINESS_INTERNAL_NAMES.electricians,
  [CONTRACTOR_SUB_INDUSTRY_IDS.dryWall]:
    CONTRACTOR_BUSINESS_INTERNAL_NAMES.dryWall,
  [CONTRACTOR_SUB_INDUSTRY_IDS.painters]:
    CONTRACTOR_BUSINESS_INTERNAL_NAMES.painters,
  [CONTRACTOR_SUB_INDUSTRY_IDS.concretors]:
    CONTRACTOR_BUSINESS_INTERNAL_NAMES.concretors,
  [CONTRACTOR_SUB_INDUSTRY_IDS.hvac]: CONTRACTOR_BUSINESS_INTERNAL_NAMES.hvac,
  [CONTRACTOR_SUB_INDUSTRY_IDS.plumber]:
    CONTRACTOR_BUSINESS_INTERNAL_NAMES.plumber,
  [CONTRACTOR_SUB_INDUSTRY_IDS.roofer]:
    CONTRACTOR_BUSINESS_INTERNAL_NAMES.roofer,
  [CONTRACTOR_SUB_INDUSTRY_IDS.generalContractor]:
    CONTRACTOR_BUSINESS_INTERNAL_NAMES.generalContractor,
  [CONTRACTOR_SUB_INDUSTRY_IDS.other]: CONTRACTOR_BUSINESS_INTERNAL_NAMES.other,
};

export const PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS = {
  mortgageBrokers: AxisStructuredDataSubIndustry.MortgageBrokers,
  realEstateBrokerages: AxisStructuredDataSubIndustry.RealEstateBrokerages,
  financialAdvisors: AxisStructuredDataSubIndustry.FinancialAdvisors,
  lawFirms: AxisStructuredDataSubIndustry.LawFirms,
  insuranceBrokerages: AxisStructuredDataSubIndustry.InsuranceBrokerages,
  architects: AxisStructuredDataSubIndustry.Architects,
  engineers: AxisStructuredDataSubIndustry.Engineers,
  other: AxisStructuredDataSubIndustry.Other,
};

export const PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_OPTIONS: Array<
  SelectableOption<AxisSubIndustryId>
> = [
  {
    id: PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS.mortgageBrokers,
    label: 'Mortgage Brokers',
  },
  {
    id: PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS.realEstateBrokerages,
    label: 'Real Estate Brokerages',
  },
  {
    id: PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS.financialAdvisors,
    label: 'Financial Advisors',
  },
  {
    id: PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS.lawFirms,
    label: 'Law Firms',
  },
  {
    id: PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS.insuranceBrokerages,
    label: 'Insurance Brokerages',
  },
  {
    id: PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS.architects,
    label: 'Architects',
  },
  {
    id: PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS.engineers,
    label: 'Engineers',
  },
  { id: PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS.other, label: 'Other' },
];

export const COMMERCIAL_INDUSTRY_OPTIONS: Array<CommercialIndustryOption> = [
  {
    id: COMMERCIAL_INDUSTRY_IDS.agricultureFood,
    label: 'Agriculture & Food',
  },
  { id: COMMERCIAL_INDUSTRY_IDS.aviation, label: 'Commercial Aviation' },
  { id: COMMERCIAL_INDUSTRY_IDS.cannabis, label: 'Cannabis' },
  { id: COMMERCIAL_INDUSTRY_IDS.construction, label: 'Construction' },
  {
    id: CONTRACTORS_INDUSTRY_ID,
    label: 'Contractors',
    subIndustries: CONTRACTOR_SUB_INDUSTRY_OPTIONS,
  },
  { id: COMMERCIAL_INDUSTRY_IDS.energy, label: 'Energy' },
  { id: COMMERCIAL_INDUSTRY_IDS.lifeSciences, label: 'Life Sciences' },
  { id: COMMERCIAL_INDUSTRY_IDS.manufacturing, label: 'Manufacturing' },
  { id: COMMERCIAL_INDUSTRY_IDS.mining, label: 'Mining' },
  { id: COMMERCIAL_INDUSTRY_IDS.nonProfit, label: 'Non-Profit' },
  {
    id: PROFESSIONAL_FINANCIAL_SERVICES_INDUSTRY_ID,
    label: 'Professional & Financial Services',
    subIndustries: PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_OPTIONS,
  },
  { id: COMMERCIAL_INDUSTRY_IDS.publicSector, label: 'Public Sector' },
  { id: COMMERCIAL_INDUSTRY_IDS.realEstate, label: 'Real Estate' },
  { id: COMMERCIAL_INDUSTRY_IDS.technology, label: 'Technology' },
  { id: COMMERCIAL_INDUSTRY_IDS.transportation, label: 'Transportation' },
  { id: COMMERCIAL_INDUSTRY_IDS.international, label: 'International' },
  { id: COMMERCIAL_INDUSTRY_IDS.other, label: 'Other' },
];

export const commercialIndustryRequiresSubIndustry = (
  industryId: AxisIndustryId,
): boolean => {
  const industry = COMMERCIAL_INDUSTRY_OPTIONS.find(
    (option) => option.id === industryId,
  );
  return Boolean(industry?.subIndustries?.length);
};

export const getCommercialSubIndustryOptions = (
  industryId: AxisIndustryId,
): Array<SelectableOption<AxisSubIndustryId>> => {
  const industry = COMMERCIAL_INDUSTRY_OPTIONS.find(
    (option) => option.id === industryId,
  );
  return industry?.subIndustries ?? [];
};

export const isContractorsIndustry = (industryId: AxisIndustryId): boolean =>
  industryId === CONTRACTORS_INDUSTRY_ID;

export const resolveNewClientFormInternalName = (
  clientType: ClientType,
  industryId?: AxisIndustryId | null,
): string => {
  if (clientType === CLIENT_TYPE_IDS.commercial) {
    return AXIS_NEW_CLIENT_FORM_INTERNAL_NAMES.commercialOrBenefits;
  }
  if (
    clientType === CLIENT_TYPE_IDS.lwb &&
    (industryId === LWB_INDUSTRY_IDS.benefits ||
      industryId === LWB_INDUSTRY_IDS.wealthInsurance)
  ) {
    return AXIS_NEW_CLIENT_FORM_INTERNAL_NAMES.lwbBenefitsOrWealth;
  }
  return AXIS_NEW_CLIENT_FORM_INTERNAL_NAMES.personalLifeOrWealth;
};

export const getContractorBusinessInternalName = (
  subIndustryId: AxisSubIndustryId,
): string | null => {
  return (
    CONTRACTOR_SUB_INDUSTRY_TO_BUSINESS_INTERNAL_NAME[subIndustryId] ?? null
  );
};
